import { React, Fragment, PropTypes, reduxConnect, styled, css, useEffect } from '../lib/imports';

import { Spacing, SectionTitle, TextBlock, AlertBanner, ErrorModal } from '../components';
import {
  setSlotToOpen,
  setSlotToClosed,
  setPlayerToPicked,
  setPlaceholderState,
  clearSubmissionError,
} from './actions';
import Slot from '../picks/Slot';
import ConfirmPicks from '../picks/ConfirmPicks';
import { isPlayerUnavailableError } from './utils';
import { useCdnImage, getAssetSet } from '../app/context/config';
import AutoPicks from './AutoPicks';
import Reminders from '../reminders/Reminders';

export const containerBaseStyles = ({ theme }) => css`
  border-bottom: 1px solid ${theme.colors.n500};
`;

export const Container = styled('div')(containerBaseStyles);

const styledTextBlockBaseStyles = () => css`
  padding: 0 20px;
  font-size: 12px;
`;

const StyledTextBlock = styled(TextBlock)(styledTextBlockBaseStyles);

const assetSet = (filename) => getAssetSet(useCdnImage, filename);

const Picks = ({
  isPlaceholder,
  isPicksFocused,
  randomPicksSelected,
  pickClicked,
  scrollPosition,
  title,
  slots,
  slotOpenId,
  picked,
  isSubmitted,
  termsText,
  successText,
  handleSlotClick,
  handlePlayerPickClick,
  handlePlaceholderState,
  handleSlotClose,
  showScoutPicks,
  showReminders,
  submissionError,
  strings,
  handleClearSubmissionError,
  confirmText,
  allSlotsPicked,
}) => {
  const defaultAssetUrl = assetSet(['hockeySticksSmall']);

  useEffect(() => {
    if (!isPicksFocused || !isPlaceholder[1] || !isPlaceholder[2] || !isPlaceholder[3]) {
      return;
    }

    if (slotOpenId) {
      handleSlotClose(slotOpenId);
    }

    setTimeout(() => handlePlaceholderState(1), 400);
    setTimeout(() => handlePlaceholderState(2), 1100);
    setTimeout(() => handlePlaceholderState(3), 1700);
  }, [isPicksFocused, randomPicksSelected, isPlaceholder]);

  useEffect(() => {
    // TODO: remove because this doesn't seem like it is being used
    if (!isPicksFocused || !slotOpenId || !pickClicked) {
      return;
    }
    handleSlotClose(slotOpenId);
  }, [slotOpenId, isPicksFocused, pickClicked]);

  const isPlayerUnavailable = isPlayerUnavailableError(submissionError);

  const errorTitle = isPlayerUnavailable
    ? strings['notifications/submission/badPlayerData/title']
    : strings['notifications/picks/submission/error/title'];
  const errorMessage = isPlayerUnavailable
    ? strings['notifications/submission/badPlayerData/text']
    : strings['notifications/picks/submission/error/text'];
  const errorButtonText = strings['notifications/errormodal/button'];

  return (
    <Container>
      <Spacing sizeBottom='mega'>
        <SectionTitle text={title} />
      </Spacing>

      {isSubmitted && (
        <Spacing sizeBottom='mega' sizeLeft='kilo' sizeRight='kilo'>
          <AlertBanner text={successText} />
        </Spacing>
      )}

      <Spacing sizeLeft='kilo' sizeRight='kilo' sizeBottom='mega'>
        {slots.map(({ id, ...props }) => (
          <Slot
            key={`slot-${id}`}
            id={id}
            slotId={id}
            isPlaceholder={isPlaceholder[id]}
            assetUrl={defaultAssetUrl}
            handleDrawerToggle={handleSlotClick}
            selectedItem={picked[id]}
            drawerDefault='closed'
            drawerOpen={id === slotOpenId}
            isSubmitted={isSubmitted}
            headerAssetHasPadding={!picked[id]}
            handleItemClick={(playerId) => {
              handleSlotClose(id);

              return handlePlayerPickClick({
                id,
                playerId,
              });
            }}
            {...props}
          />
        ))}
      </Spacing>

      {showScoutPicks && <AutoPicks />}

      {showReminders && <Reminders />}

      {!isSubmitted && (
        <>
          <Spacing sizeBottom='mega'>
            <StyledTextBlock text={termsText} align='center' />
          </Spacing>
          <Spacing sizeBottom='giga'>
            <ConfirmPicks />
            {allSlotsPicked && (
              <Spacing sizeTop='kilo' sizeLeft='bit' sizeRight='bit'>
                <TextBlock noMargin text={confirmText} align='center' size='kilo' bold />
              </Spacing>
            )}
          </Spacing>
        </>
      )}

      {submissionError && (
        <ErrorModal
          title={errorTitle}
          message={errorMessage}
          buttonText={errorButtonText}
          onClose={handleClearSubmissionError}
        />
      )}
    </Container>
  );
};

Picks.propTypes = {
  isPlaceholder: PropTypes.object,
  isPicksFocused: PropTypes.bool,
  randomPicksSelected: PropTypes.bool,
  pickClicked: PropTypes.bool,
  scrollPosition: PropTypes.number,
  successText: PropTypes.string,
  title: PropTypes.string,
  termsText: PropTypes.string,
  slots: PropTypes.array,
  slotOpenId: PropTypes.number,
  picked: PropTypes.object,
  isSubmitted: PropTypes.bool,
  handleSlotClick: PropTypes.func,
  handlePlayerPickClick: PropTypes.func,
  handlePlaceholderState: PropTypes.func,
  handleSlotClose: PropTypes.func,
  platform: PropTypes.string,
  language: PropTypes.string,
  submissionError: PropTypes.object,
  strings: PropTypes.object,
  handleClearSubmissionError: PropTypes.func,
};

const mapStateToProps = (state) => {
  const { strings, picks, players, ui } = state;
  const {
    isPlaceholder,
    picked,
    slotOpenId,
    picksLocked,
    randomPickCount,
    randomPicksSelected,
    pickClicked,
    submissionError,
  } = picks;
  const { playersPicked } = players;
  const { isFocused, componentSize, notificationBarOpen, isScrolling } = ui;
  const { picksScrollPosition } = componentSize;
  const { withNotification, withoutNotification } = picksScrollPosition;

  const { slotCount } = picks;

  const slotsPicked = Object.keys(picked)
    .map((id) => picked[id])
    .filter((id) => id);

  const allSlotsPicked = slotsPicked.length === slotCount;

  const allStrings = {
    title: strings['titles/section/picks/text'],
    termsText: strings['global/termsAndPrivacy/text'](
      'timhortons://terms-of-use',
      'timhortons://account/terms-and-privacy',
    ),
    submitBtnText: strings['button/confirmPlayers/text'],
    successText: strings['button/submitted/text'],
    confirmText: strings['notifications/confirmPicks/text'],
  };

  const slots = [
    {
      id: 1,
      subText: strings['global/slots/playerSelect/text'],
      text: strings['global/slots/number/text'](1),
      showToggleIcon: true,
    },
    {
      id: 2,
      subText: strings['global/slots/playerSelect/text'],
      text: strings['global/slots/number/text'](2),
      showToggleIcon: true,
    },
    {
      id: 3,
      subText: strings['global/slots/playerSelect/text'],
      text: strings['global/slots/number/text'](3),
      showToggleIcon: true,
    },
  ];

  const scrollPosition = notificationBarOpen ? withNotification : withoutNotification;

  return {
    ...allStrings,
    isPlaceholder,
    scrollPosition,
    isScrolling,
    slots,
    slotOpenId,
    picked,
    randomPickCount,
    randomPicksSelected,
    pickClicked,
    isPicksFocused: isFocused.picks,
    isSubmitted: picksLocked || playersPicked,
    submissionError,
    strings,
    allSlotsPicked,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handleSlotClick: (id) => dispatch(setSlotToOpen(id)),
    handleSlotClose: (id) => dispatch(setSlotToClosed(id)),
    handlePlayerPickClick: (props) => dispatch(setPlayerToPicked(props)),
    handlePlaceholderState: (slotId) => dispatch(setPlaceholderState(slotId)),
    handleClearSubmissionError: () => dispatch(clearSubmissionError()),
  };
};

export default reduxConnect(mapStateToProps, mapDispatchToProps)(Picks);
